import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { format } from 'date-fns';
import { Clock, Users, DollarSign, Edit, Calendar } from 'lucide-react';

interface EditSlotModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (slotData: any) => Promise<void>;
  slotData: {
    id: string;
    title: string;
    start: Date | string;
    end?: Date | string;
    capacity: number;
    booked: number;
    price?: number;
    activityId?: string;
  } | null;
  isLoading?: boolean;
}

export function EditSlotModal({ isOpen, onClose, onSave, slotData, isLoading = false }: EditSlotModalProps) {
  const [formData, setFormData] = useState({
    startTime: '',
    endTime: '',
    capacity: 10,
    price: 99.99,
    status: 'available' as 'available' | 'fully-booked' | 'cancelled'
  });

  useEffect(() => {
    if (slotData) {
      const startDate = new Date(slotData.start);
      const endDate = slotData.end ? new Date(slotData.end) : new Date(startDate.getTime() + 2 * 60 * 60 * 1000);
      
      setFormData({
        startTime: format(startDate, 'HH:mm'),
        endTime: format(endDate, 'HH:mm'),
        capacity: slotData.capacity || 10,
        price: slotData.price || 99.99,
        status: slotData.booked >= slotData.capacity ? 'fully-booked' : 'available'
      });
    }
  }, [slotData]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!slotData) return;

    try {
      await onSave({
        id: slotData.id,
        startTime: formData.startTime,
        endTime: formData.endTime,
        capacity: formData.capacity,
        price: formData.price,
        status: formData.status
      });
      onClose();
    } catch (error) {
      console.error('Error saving slot:', error);
    }
  };

  if (!slotData) return null;

  const startDate = new Date(slotData.start);
  const bookingPercentage = (slotData.booked / slotData.capacity) * 100;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg border border-slate-200/60 shadow-xl">
        <DialogHeader className="pb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 border border-blue-200/50 flex items-center justify-center shadow-sm">
              <Edit className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <DialogTitle className="text-xl font-semibold text-slate-900">Edit Activity Slot</DialogTitle>
              <p className="text-sm text-slate-600 mt-1">Modify slot details and availability</p>
            </div>
          </div>
        </DialogHeader>

        {/* Slot Overview Card */}
        <Card className="border border-slate-200/60 shadow-sm bg-slate-50/50">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium text-slate-700">Current Slot Details</CardTitle>
              <Badge 
                variant="secondary" 
                className={`${
                  bookingPercentage >= 90 ? 'bg-red-100 text-red-800 border-red-200/50' :
                  bookingPercentage >= 70 ? 'bg-amber-100 text-amber-800 border-amber-200/50' :
                  bookingPercentage >= 30 ? 'bg-blue-100 text-blue-800 border-blue-200/50' :
                  'bg-emerald-100 text-emerald-800 border-emerald-200/50'
                }`}
              >
                {Math.round(bookingPercentage)}% Booked
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600">{format(startDate, 'MMM dd, yyyy')}</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-4 h-4 text-slate-500" />
                <span className="text-slate-600">{slotData.booked}/{slotData.capacity} guests</span>
              </div>
            </div>
            <div className="mt-2">
              <h4 className="font-medium text-slate-900 truncate">{slotData.title}</h4>
            </div>
          </CardContent>
        </Card>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Time Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="startTime" className="text-sm font-medium text-slate-700">
                Start Time
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="startTime"
                  type="time"
                  value={formData.startTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, startTime: e.target.value }))}
                  className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                  required
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="endTime" className="text-sm font-medium text-slate-700">
                End Time
              </Label>
              <div className="relative">
                <Clock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="endTime"
                  type="time"
                  value={formData.endTime}
                  onChange={(e) => setFormData(prev => ({ ...prev, endTime: e.target.value }))}
                  className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                  required
                />
              </div>
            </div>
          </div>

          {/* Capacity and Price */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="capacity" className="text-sm font-medium text-slate-700">
                Capacity
              </Label>
              <div className="relative">
                <Users className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="capacity"
                  type="number"
                  min="1"
                  max="100"
                  value={formData.capacity}
                  onChange={(e) => setFormData(prev => ({ ...prev, capacity: parseInt(e.target.value) || 1 }))}
                  className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                  required
                />
              </div>
              {slotData.booked > formData.capacity && (
                <p className="text-xs text-red-600">
                  Warning: Current bookings ({slotData.booked}) exceed new capacity
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="price" className="text-sm font-medium text-slate-700">
                Price ($)
              </Label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  id="price"
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))}
                  className="pl-10 border-slate-200/80 focus:border-primary focus:ring-primary/20"
                  required
                />
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status" className="text-sm font-medium text-slate-700">
              Status
            </Label>
            <Select value={formData.status} onValueChange={(value: any) => setFormData(prev => ({ ...prev, status: value }))}>
              <SelectTrigger className="border-slate-200/80 focus:border-primary focus:ring-primary/20">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="available">Available</SelectItem>
                <SelectItem value="fully-booked">Fully Booked</SelectItem>
                <SelectItem value="cancelled">Cancelled</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3 pt-4 border-t border-slate-200/60">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 border-slate-200/80 hover:bg-slate-50"
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-action-500 to-action-600 hover:from-action-600 hover:to-action-700 shadow-md hover:shadow-lg transition-shadow"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
} 